/**
 * 语言检测 + 本地化路径工具。
 *
 * 语言源 = URL 前缀 /:lang（唯一真相）。
 * - detectLang()：服务端首访 / 测试用，按 localStorage > navigator.language > 默认 推断。
 * - useLang()：组件内，从当前路由 useParams().lang 读，无效回退默认。
 * - localizedPath()：给无 lang 前缀的路径加前缀，如 "/pricing" → "/zh/pricing"。
 * - LocalizedLink：包裹 react-router Link，自动按当前语言拼 to。
 */

import { Link, useParams } from "react-router-dom";
import { SUPPORTED_LANGS, DEFAULT_LANG, type Lang } from "../i18n";

const LANG_STORAGE_KEY = "lang";

/** 判断字符串是否为支持的语言 */
export function isSupportedLang(value: unknown): value is Lang {
  return typeof value === "string" && (SUPPORTED_LANGS as readonly string[]).includes(value);
}

/**
 * 检测用户首选语言（根路径 / 重定向用）。
 * 优先级：localStorage('lang') > navigator.language 映射 > 默认 en。
 * 仅在浏览器环境生效；SSR/测试无 navigator 时回退默认。
 * 默认 en（英语为主市场，见 SEO_CONTENT_PLAN.md §4.4）。
 *
 * 浏览器语言映射规则（小写后匹配，取最长前缀命中）：
 *   zh-*                    → zh（简体）
 *   ja-*                    → ja
 *   ko-*                    → ko
 *   en-*                    → en
 *   其余                    → en（DEFAULT_LANG，主市场为英语）
 */
export function detectLang(): Lang {
  // 1. localStorage 偏好
  try {
    const saved = localStorage.getItem(LANG_STORAGE_KEY);
    if (isSupportedLang(saved)) return saved;
  } catch {
    // localStorage 不可用（隐私模式等）→ 继续
  }
  // 2. 浏览器语言映射
  if (typeof navigator !== "undefined") {
    const nav = navigator.language?.toLowerCase() ?? "";
    if (nav.startsWith("zh")) return "zh";
    if (nav.startsWith("ja")) return "ja";
    if (nav.startsWith("ko")) return "ko";
    if (nav.startsWith("en")) return "en";
  }
  // 3. 默认
  return DEFAULT_LANG;
}

/** 记住用户选择（LocaleSwitcher 切换时调） */
export function rememberLang(lang: Lang): void {
  try {
    localStorage.setItem(LANG_STORAGE_KEY, lang);
  } catch {
    // 忽略写入失败
  }
}

/**
 * 取当前语言（组件内）。
 * 读 useParams().lang；/:lang 路由内即为该值。
 * 无前缀路由（/blog 等英文独占页）回退 detectLang()——与根路径 `/` 重定向、
 * Clerk 初始语言（main.tsx initialClerkLang）同一约定：localStorage 记忆
 * （LangSync 每次 /:lang 访问都会写入）> 浏览器语言 > 默认。
 * 不得硬编码 DEFAULT_LANG：否则从 /zh 进入 /blog 后，顶栏锚点链接会拼成
 * /en#…，点回去触发 LangSync 把整站切成英文。
 */
export function useLang(): Lang {
  const { lang } = useParams();
  return isSupportedLang(lang) ? lang : detectLang();
}

/**
 * 给路径加语言前缀。
 * - 已含前缀（/zh、/en 开头）→ 原样返回。
 * - 无前缀 → 加上。
 * localizedPath("/pricing", "zh") → "/zh/pricing"
 * localizedPath("/zh/pricing", "en") → "/zh/pricing"（已含，不动）
 */
export function localizedPath(path: string, lang: Lang = DEFAULT_LANG): string {
  // 去掉末尾斜杠（除根外），统一处理
  const normalized = path !== "/" && path.endsWith("/") ? path.slice(0, -1) : path;
  // 已带 /zh 或 /en 前缀
  for (const l of SUPPORTED_LANGS) {
    if (normalized === `/${l}` || normalized.startsWith(`/${l}/`)) {
      return normalized;
    }
  }
  // 根路径
  if (normalized === "" || normalized === "/") {
    return `/${lang}`;
  }
  return `/${lang}${normalized.startsWith("/") ? "" : "/"}${normalized}`;
}

/**
 * 去掉路径的语言前缀，返回「无前缀路径」。
 * stripLang("/zh/pricing") → "/pricing"；"/pricing" → "/pricing"；"/zh" → ""。
 * 用于切换语言时保持当前页面（拼新前缀）。
 */
export function stripLang(path: string): string {
  for (const l of SUPPORTED_LANGS) {
    if (path === `/${l}`) return "";
    if (path.startsWith(`/${l}/`)) return path.slice(`/${l}`.length);
  }
  return path;
}

/** LocalizedLink：自动按当前语言拼前缀的 <Link>。 */
export function LocalizedLink({
  to,
  children,
  ...rest
}: React.ComponentProps<typeof Link>) {
  const lang = useLang();
  const target = typeof to === "string" ? localizedPath(to, lang) : to;
  return (
    <Link to={target} {...rest}>
      {children}
    </Link>
  );
}
